export const profile = {
  name: "Arjan Bajwa",
  headline: "Student Leader · Curriculum Developer · Civic Advocate",
  location: "Pennsylvania, United States",
  linkedin: "https://www.linkedin.com/in/arjan-bajwa-789b70424/",
  about:
    "I'm a student at Council Rock High School South with a passion for financial literacy, public service, and education. From designing financial curricula for Punjabi immigrant communities to serving in the Pennsylvania House of Representatives, I believe in using knowledge to create real impact, one community at a time.",
  motto: "Learn deeply. Serve widely. Lead with purpose.",
};

export const experience = [
  {
    id: "khalsa",
    testId: "experience-card-khalsa-financial",
    role: "Research & Curriculum Developer",
    company: "Khalsa Financial Care",
    type: "Self-employed · Remote",
    period: "Jun 2026 - Present",
    location: "New Jersey, United States",
    description:
      "Designed a financial literacy curriculum built specifically for Punjabi immigrants in the United States, covering credit, banking, taxes, and long-term savings in language and examples that actually resonate. Taught 1,100+ immigrants through 30+ free workshops held in community spaces and Gurdwaras, and the work has been recognized by the NJ General Assembly. The curriculum is approved by a Vice President of Community Development Banking at JPMorgan Chase and a Certified Financial Coach. Also built and launched PolicyPulse, a financial policy survey platform that studies how different age groups make financial decisions in response to public policy changes, giving researchers and policymakers real behavioral data to work with.",
    link: { url: "https://policypulseresearch.com/", label: "Visit PolicyPulse" },
    skills: ["Project Management", "Communication", "Curriculum Design"],
    icon: "Landmark",
  },
  {
    id: "sikh-sabha",
    testId: "experience-card-sikh-sabha",
    role: "Assistant Teacher, Punjabi Language",
    company: "Sikh Sabha of New Jersey",
    type: "Volunteer · On-site",
    period: "Oct 2024 - Mar 2026",
    location: "Lawrenceville, New Jersey",
    description:
      "Taught Punjabi reading and writing at the Sikh Sabha Gurdwara's Sunday school from October 2024 to March 2026. Classes brought together students of all ages, including seniors who never got the chance to learn Punjabi earlier in their lives, so lessons were shaped for first-time learners at every stage of life. The work keeps an entire community connected to its language, script, and heritage, one class at a time.",
    skills: ["Teaching", "Punjabi Language", "Community Service"],
    icon: "BookOpen",
  },
  {
    id: "blackrock",
    testId: "experience-card-blackrock",
    role: "Extern",
    company: "BlackRock",
    type: "Externship · On-site",
    period: "Jun 2026",
    location: "New York City, New York",
    description:
      "Selected personally by BlackRock's COO, based on a demonstrated interest in finance and a track record of uplifting the community, to visit BlackRock headquarters in New York City. Spent the day inside the world's largest asset manager, learning how a firm overseeing trillions in assets is actually structured, how teams across research, risk, and client work fit together, and what it takes to operate at that scale. Walked away with a clearer picture of where a career in finance can lead and a stronger conviction that financial knowledge should reach the communities that need it most.",
    skills: ["Finance", "Asset Management", "Networking"],
    icon: "Briefcase",
  },
  {
    id: "kumon",
    testId: "experience-card-kumon-north-america",
    role: "Tutor",
    company: "Kumon North America, Inc.",
    type: "Part-time · On-site",
    period: "Aug 2022 - Jun 2026",
    location: "Southampton, Pennsylvania",
    description:
      "Educated 200+ students ages 4 to 18 in Math and English over nearly four years of part-time work. Worked with everyone from kindergartners learning to read to teenagers prepping for advanced math, adapting the approach for each learner. Graded, tracked progress, and coached students through frustration until concepts clicked, building the patience and communication skills that now carry into every teaching and mentoring role.",
    skills: ["Tutoring", "Organization", "Mentorship"],
    icon: "GraduationCap",
  },
  {
    id: "house",
    testId: "experience-card-pa-house-representatives",
    role: "Legislative Intern",
    company: "Office of Rep. Brian Munroe · PA House of Representatives",
    type: "Internship · On-site",
    period: "Jun 2025 - Aug 2025",
    location: "Warminster, Pennsylvania",
    description:
      "Worked inside a state representative's district office, informing and educating constituents on state protected services and helping residents navigate the help available to them. Knocked on 500+ doors across the district to hear local issues firsthand, bringing real concerns from kitchen tables back to Rep. Munroe's office. Ran and managed the office's employment event, recruiting local businesses and inviting residents to meet them face to face, which turned into successful job fairs for the community. The summer was a crash course in how policy actually touches people's lives, one conversation at a time.",
    skills: ["Legislative Affairs", "Community Outreach"],
    icon: "Building2",
  },
];

export const education = {
  school: "Council Rock High School South",
  program: "General Studies",
  period: "2023 - Present · Class of 2027",
  highlights: [
    "4.25 Weighted GPA",
    "National Honor Society member",
    "French Honor Society Officer",
    "Distinguished Honor Roll in every marking period since 7th grade",
    "Model UN Delegate representing 3 nations in 3 committees",
    "Southeastern Pennsylvania Debate League Quarterfinalist",
  ],
};

export const skills = [
  { name: "Legislative Affairs", context: "PA House of Representatives", category: "Civic" },
  { name: "Community Outreach", context: "Constituent engagement & job fairs", category: "Civic" },
  { name: "Public Speaking & Debate", context: "SPDL Quarterfinalist", category: "Civic" },
  { name: "Curriculum Design", context: "Khalsa Financial Care", category: "Education" },
  { name: "Tutoring", context: "200+ students, ages 4 to 18", category: "Education" },
  { name: "Mentorship", context: "Sunday school & tutoring", category: "Education" },
  { name: "Project Management", context: "#PhidogenChase initiative", category: "Leadership" },
  { name: "Communication", context: "Multilingual community instruction", category: "Leadership" },
  { name: "Organization", context: "Multi-role balance & planning", category: "Leadership" },
  { name: "Research", context: "Financial confidence surveying", category: "Leadership" },
];

export const gallery = [
  {
    id: "portrait",
    src: "/images/arjan-headshot.jpg",
    tag: "Milestone",
    caption: "Ready for the next chapter",
    wide: false,
  },
  {
    id: "pa-house",
    src: "/images/pa-house-interns.jpg",
    tag: "PA House",
    caption: "On the House floor in Harrisburg with Rep. Brian Munroe and fellow interns",
    wide: true,
  },
  {
    id: "blackrock",
    src: "https://customer-assets-wrfwihn1.emergentagent.net/job_my-story-hub-9/artifacts/7umf6kvx_IMG_6496.webp",
    tag: "Finance",
    caption: "Extern at BlackRock HQ, selected by the COO to see how the world's largest asset manager operates",
    wide: false,
  },
  {
    id: "seva-prep",
    src: "https://customer-assets-wrfwihn1.emergentagent.net/job_my-story-hub-9/artifacts/wpj41w2n_IMG_8128.webp",
    tag: "Seva",
    caption: "Community kitchen service, preparing meals for the community",
    wide: true,
  },
  {
    id: "seva-serve",
    src: "https://customer-assets-wrfwihn1.emergentagent.net/job_my-story-hub-9/artifacts/8439bchj_IMG_8131.webp",
    tag: "Service",
    caption: "Hands-on volunteering that earned the Presidential Gold Award",
    wide: false,
  },
  {
    id: "debate",
    src: "https://customer-assets-wrfwihn1.emergentagent.net/job_my-story-hub-9/artifacts/3yqmnol0_IMG_2482.webp",
    tag: "Speaking",
    caption: "Presenting at the Gurdwara, teaching financial literacy to the community",
    wide: false,
  },
  {
    id: "fl-workshop",
    src: "https://customer-assets-wrfwihn1.emergentagent.net/job_my-story-hub-9/artifacts/jmn1r22e_Financial%20Literacy.png",
    tag: "Khalsa",
    caption: "Leading one of 30+ free financial literacy workshops at the Gurdwara, teaching credit, banking, and savings to the community",
    wide: true,
  },
  {
    id: "fl-team",
    src: "https://customer-assets-wrfwihn1.emergentagent.net/job_my-story-hub-9/artifacts/qeqsg3zu_Financial%20litracy.png",
    tag: "Khalsa",
    caption: "With the Khalsa Financial Care team after a Foundations of Financial Literacy session",
    wide: false,
  },
  {
    id: "saldef",
    src: "https://customer-assets-wrfwihn1.emergentagent.net/job_my-story-hub-9/artifacts/2f446o39_SALDEF%20%20I%20am%20the%20left%20most.png",
    tag: "SALDEF",
    caption: "With the SALDEF cohort (far left), building the next generation of Sikh American leaders",
    wide: true,
  },
  {
    id: "bhangra",
    src: "https://customer-assets-wrfwihn1.emergentagent.net/job_my-story-hub-9/artifacts/jitj77e8_Bhanga%20Team%20Member%20I%20am%20in%20Middle.jpeg",
    tag: "Culture",
    caption: "On stage with my Bhangra team (center), keeping Punjabi culture alive one performance at a time",
    wide: false,
  },
  {
    id: "community-qa",
    src: "https://customer-assets-wrfwihn1.emergentagent.net/job_my-story-hub-9/artifacts/87jy1z1p_Conducted%20a%20community%20event%20to%20get%20Punjabi%20families%20q%26a%20with%20students%20from%20top%20universities.png",
    tag: "Community",
    caption: "Organized a community Q&A connecting Punjabi families with students from top universities",
    wide: true,
  },
  {
    id: "pa-session-video",
    type: "video",
    src: "/videos/pa-house-session.mp4",
    poster: "/images/pa-session-poster.jpg",
    tag: "PA House",
    caption: "Inside the Pennsylvania House chamber on session day",
    wide: true,
  },
  {
    id: "seva-video",
    type: "video",
    src: "/videos/seva-kitchen.mp4",
    poster: "/images/seva-video-poster.jpg",
    tag: "Seva",
    caption: "Behind the scenes in the community kitchen",
    wide: true,
  },
];

export const activities = [
  {
    id: "active-impact",
    testId: "activity-card-active-impact",
    role: "Founder & President",
    org: "Active Impact Club",
    description: "Founded and leads one of the biggest clubs at Council Rock South, 200+ members strong, organizing fundraising drives and campaigns that turn student energy into real dollars for causes that matter.",
    icon: "Megaphone",
    tag: "Leadership",
  },
  {
    id: "model-un",
    testId: "activity-card-model-un",
    role: "Delegate",
    org: "Model United Nations",
    description: "Represented 3 nations across 3 different committees, drafting resolutions, building blocs, and debating global policy.",
    icon: "Globe2",
    tag: "Diplomacy",
  },
  {
    id: "french-honor",
    testId: "activity-card-french-honor",
    role: "Officer",
    org: "French Honor Society",
    description: "Officer of the Société Honoraire de Français, on track for president next year, celebrating French language and culture.",
    icon: "BookOpen",
    tag: "Academics",
  },
  {
    id: "volunteens",
    testId: "activity-card-volunteens",
    role: "Officer",
    org: "Volunteens Club",
    description: "Officer of the high school volunteering club serving the local Gurdwara, coordinating seva and community events.",
    icon: "HandHeart",
    tag: "Service",
  },
  {
    id: "sikhlead",
    testId: "activity-card-sikhlead",
    role: "2025 Regional Cohort",
    org: "SikhLEAD",
    description: "Selected for SikhLEAD's 2025 Regional Cohort, a leadership development program for the next generation of Sikh American leaders.",
    icon: "Users",
    tag: "Leadership",
  },
  {
    id: "debate",
    testId: "activity-card-debate",
    role: "Quarterfinalist",
    org: "Southeastern Pennsylvania Debate League",
    description: "Quarter-finalist in SPDL representing Council Rock High School South, sharpening the argumentation and public speaking skills powering everything else.",
    icon: "Mic",
    tag: "Speech",
  },
  {
    id: "bhangra",
    testId: "activity-card-bhangra",
    role: "Team Member",
    org: "Bhangra Team",
    description: "Performs with a competitive Bhangra team, celebrating Punjabi culture on stage and keeping folk dance traditions alive for the next generation.",
    icon: "Music",
    tag: "Culture",
  },
];

export const honors = [
  {
    id: "presidential",
    testId: "honor-card-presidential-service-award",
    title: "Presidential Volunteer Service Award",
    subtitle: "Gold Medalist",
    issuer: "Issued by Joseph Biden",
    date: "Feb 2024",
    description:
      "A civil award bestowed by the President of the United States to honor individuals, families, and organizations that provide hundreds of hours of community service.",
    icon: "Medal",
  },
  {
    id: "nhs",
    testId: "honor-card-national-honor-society",
    title: "National Honor Society",
    subtitle: "Council Rock HS South Chapter",
    issuer: "Issued by Ronn Nozoe",
    date: "May 2026",
    description:
      "Selected as a member of the Council Rock High School South chapter of the National Honor Society.",
    icon: "Award",
  },
  {
    id: "helper",
    testId: "honor-card-helper-helper-service",
    title: "Helper Helper National Service Honor",
    subtitle: "Gold Medalist",
    issuer: "Issued by Krista Clement",
    date: "Oct 2025",
    description:
      "A recognition of outstanding commitment to community service, leadership through volunteerism, and dedication to making a positive impact.",
    icon: "HeartHandshake",
  },
  {
    id: "sikhlead",
    testId: "honor-card-sikhlead-internship",
    title: "SikhLEAD Regional Internship Program",
    subtitle: "Program Graduate",
    issuer: "Issued by Puneet Kaur | Kiran Kaur Gill",
    date: "Jul 2025",
    description:
      "Completed the SikhLEAD Regional Internship Program, a leadership development program for the next generation of Sikh American leaders.",
    icon: "Users",
  },
  {
    id: "spdl",
    testId: "honor-card-spdl-quarterfinalist",
    title: "Southeastern Pennsylvania Debate League",
    subtitle: "Quarterfinalist",
    issuer: "Issued by Southeastern Pennsylvania Debate League",
    date: "Feb 2025",
    description:
      "Quarter-finalist in SPDL representing Council Rock High School South.",
    icon: "Trophy",
  },
  {
    id: "honor-roll",
    testId: "honor-card-distinguished-honor-roll",
    title: "Distinguished Honor Roll",
    subtitle: "Every Marking Period Since 7th Grade",
    issuer: "Council Rock School District",
    date: "2019 - Present",
    description:
      "An unbroken streak of top academic performance across every single marking period for six consecutive years.",
    icon: "Star",
  },
];
